import React, { useState } from 'react';
import FileUpload from './FileUpload';
import ContentEditor from './ContentEditor';
import ContactEmail from './ContactEmail';
import RecycleBinManager from './RecycleBinManager';
import IconManager from './IconManager';
import DesktopBackground from './DesktopBackground';

interface DashboardProps {
  onLogout: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState<'gallery' | 'videos' | 'music' | 'content' | 'contact' | 'recycle' | 'icons' | 'background' | 'settings'>('content');

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: '#c0c0c0'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(to right, #000080, #1084d0)',
        color: '#fff',
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '2px solid #808080'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '16px' }}>⚙️ Control Panel - AI Access Consulting</h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '11px', opacity: 0.9 }}>
            Windows 95 Admin Interface
          </p>
        </div>
        <button onClick={onLogout}>Logout</button>
      </div>

      {/* Main content */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <div style={{
          width: '200px',
          background: '#c0c0c0',
          borderRight: '2px solid #808080',
          padding: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <button
            onClick={() => setActiveTab('content')}
            style={{
              textAlign: 'left',
              background: activeTab === 'content' ? '#000080' : undefined,
              color: activeTab === 'content' ? '#fff' : undefined
            }}
          >
            📝 About Me Text
          </button>
          <button
            onClick={() => setActiveTab('contact')}
            style={{
              textAlign: 'left',
              background: activeTab === 'contact' ? '#000080' : undefined,
              color: activeTab === 'contact' ? '#fff' : undefined
            }}
          >
            📧 Contact Email
          </button>
          <div style={{ height: '2px', background: '#808080', margin: '4px 0' }} />
          <button
            onClick={() => setActiveTab('gallery')}
            style={{
              textAlign: 'left',
              background: activeTab === 'gallery' ? '#000080' : undefined,
              color: activeTab === 'gallery' ? '#fff' : undefined
            }}
          >
            🖼️ Gallery Images
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            style={{
              textAlign: 'left',
              background: activeTab === 'videos' ? '#000080' : undefined,
              color: activeTab === 'videos' ? '#fff' : undefined
            }}
          >
            🎬 Videos
          </button>
          <button
            onClick={() => setActiveTab('music')}
            style={{
              textAlign: 'left',
              background: activeTab === 'music' ? '#000080' : undefined,
              color: activeTab === 'music' ? '#fff' : undefined
            }}
          >
            🎵 Music
          </button>
          <div style={{ height: '2px', background: '#808080', margin: '4px 0' }} />
          <button
            onClick={() => setActiveTab('recycle')}
            style={{
              textAlign: 'left',
              background: activeTab === 'recycle' ? '#000080' : undefined,
              color: activeTab === 'recycle' ? '#fff' : undefined
            }}
          >
            🗑️ Recycle Bin
          </button>
          <button
            onClick={() => setActiveTab('icons')}
            style={{
              textAlign: 'left',
              background: activeTab === 'icons' ? '#000080' : undefined,
              color: activeTab === 'icons' ? '#fff' : undefined
            }}
          >
            🖥️ Desktop Icons
          </button>
          <button
            onClick={() => setActiveTab('background')}
            style={{
              textAlign: 'left',
              background: activeTab === 'background' ? '#000080' : undefined,
              color: activeTab === 'background' ? '#fff' : undefined
            }}
          >
            🖼️ Desktop Background
          </button>
          <div style={{ height: '2px', background: '#808080', margin: '4px 0' }} />
          <button
            onClick={() => setActiveTab('settings')}
            style={{
              textAlign: 'left',
              background: activeTab === 'settings' ? '#000080' : undefined,
              color: activeTab === 'settings' ? '#fff' : undefined
            }}
          >
            ⚙️ Settings
          </button>
          <div style={{ flex: 1 }} />
          <a href="/" target="_blank" style={{ fontSize: '11px', textAlign: 'center', padding: '8px' }}>
            🖥️ View Desktop
          </a>
        </div>

        {/* Content area */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
          {activeTab === 'content' && <ContentEditor />}
          {activeTab === 'contact' && <ContactEmail />}
          {activeTab === 'gallery' && <FileUpload type="images" title="Gallery Images" accept="image/*" />}
          {activeTab === 'videos' && <FileUpload type="videos" title="Videos" accept="video/*" />}
          {activeTab === 'music' && <FileUpload type="music" title="Music Files" accept="audio/*" />}
          {activeTab === 'recycle' && <RecycleBinManager />}
          {activeTab === 'icons' && <IconManager />}
          {activeTab === 'background' && <DesktopBackground />}
          {activeTab === 'settings' && <SettingsPanel />}
        </div>
      </div>
    </div>
  );
};

const SettingsPanel: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(data.error || 'Failed to change password');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  return (
    <div className="window" style={{ maxWidth: '600px' }}>
      <div className="title-bar">
        <div className="title-bar-text">Settings</div>
      </div>
      <div className="window-body" style={{ padding: '16px' }}>
        <h3>Change Password</h3>
        <form onSubmit={handleChangePassword}>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px' }}>
              Current Password:
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              style={{ width: '100%', padding: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px' }}>
              New Password:
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              style={{ width: '100%', padding: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px' }}>
              Confirm New Password:
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              style={{ width: '100%', padding: '4px' }}
            />
          </div>

          {message && (
            <div style={{
              background: '#008000',
              color: '#fff',
              padding: '8px',
              marginBottom: '12px',
              fontSize: '11px'
            }}>
              {message}
            </div>
          )}

          {error && (
            <div style={{
              background: '#ff0000',
              color: '#fff',
              padding: '8px',
              marginBottom: '12px',
              fontSize: '11px'
            }}>
              {error}
            </div>
          )}

          <button type="submit">Change Password</button>
        </form>
      </div>
    </div>
  );
};

export default Dashboard;
